import { cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { getDefaultClassNames } from 'react-day-picker';
import { type Button, buttonVariants } from '@avanta/shared/components/ui/Button';
import { cn } from '@avanta/shared/utils/cn';

const defaultClassNames = getDefaultClassNames();

export const calendarContainer = cn(
  'bg-background group/calendar p-3 [--cell-size:--spacing(8)]',
  '[[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent',
  'rtl:**:[.rdp-button_next>svg]:rotate-180 rtl:**:[.rdp-button_previous>svg]:rotate-180'
);

export const calendarDayButton = cn(
  'flex w-9 shrink-0 items-center justify-center rounded-md',
  'border-[3px] border-transparent p-0 h-9 outline-none',
  'font-semibold text-sm leading-5 text-denim-500',
  'data-[today=true]:bg-teal-200 data-[today=true]:border-teal-100 data-[today=true]:rounded-full',
  'hover:bg-plum-200 hover:border-plum-100 hover:rounded-full hover:shadow-box-sm',
  'data-[outside=true]:text-plum-200',
  'data-[selected-single=true]:bg-teal-600 data-[selected-single=true]:border-transparent data-[selected-single=true]:ring-[3px] data-[selected-single=true]:ring-teal-200 data-[selected-single=true]:ring-offset-0 data-[selected-single=true]:rounded-full data-[selected-single=true]:text-white',
  'data-[range-middle=true]:bg-mint-300 data-[range-middle=true]:text-denim-500',
  'data-[range-start=true]:bg-teal-600 data-[range-start=true]:text-white',
  'data-[range-end=true]:bg-teal-600 data-[range-end=true]:text-white',
  'data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none',
  'data-[range-start=true]:rounded-l-md',
  'group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10',
  '[&>span]:text-xs [&>span]:opacity-70',
  'disabled:text-gray-400 disabled:font-medium disabled:opacity-100 disabled:pointer-events-none'
);

export const calendarCaptionLabel = cva(
  'select-none text-denim-800 font-medium tracking-[-0.01em]',
  {
    variants: {
      layout: {
        label: 'text-sm leading-4 am-3xl:text-base am-3xl:leading-5',
        dropdown:
          'rounded-md pl-2 pr-1 flex items-center gap-1 h-8 text-base leading-5 [&>svg]:text-gray-600 [&>svg]:size-3.5',
      },
    },
    defaultVariants: {
      layout: 'label',
    },
  }
);

export const calendarNavButton =
  'size-(--cell-size) aria-disabled:invisible p-0 select-none focus-visible:outline-none focus-visible:ring-0';
export const calendarChevron = 'size-6';
export const calendarWeekNumber = 'flex size-(--cell-size) items-center justify-center text-center';
const calendarRoot = 'w-fit';
const calendarMonths = 'flex gap-4 flex-col md:flex-row relative';
const calendarMonth = 'flex flex-col w-full gap-4 px-0';
const calendarNav = 'flex items-center gap-1 w-full p-0 absolute top-0 inset-x-0 justify-between';
const calendarMonthCaption = 'flex items-center justify-center h-(--cell-size) w-full px-0';
const calendarDropdowns =
  'w-full flex items-center justify-center h-(--cell-size) gap-1.5 text-denim-800 text-base font-medium leading-5';
const calendarDropdownRoot =
  'relative rounded-md border border-teal-200/70 shadow-xs transition-colors has-focus:border-teal-500 has-focus:ring-2 has-focus:ring-teal-200/80';
const calendarDropdown = 'absolute inset-0 opacity-0';
const calendarTable = 'w-full border-collapse';
const calendarWeekdays = 'flex';
const calendarWeekday = 'text-denim-500 rounded-md flex-1 font-medium text-sm select-none';
const calendarWeek = 'flex w-full mt-2 h-full';
const calendarWeekNumberHeader = 'select-none w-(--cell-size)';
const calendarWeekNumberCell = 'text-[0.8rem] select-none text-gray-600';
const calendarDay = 'relative p-0 flex items-center justify-center group/day select-none';
const calendarRangeStart = 'rounded-l-md bg-mint-300';
const calendarRangeMiddle = 'rounded-none';
const calendarRangeEnd = 'rounded-r-md bg-mint-300';
const calendarToday = 'rounded-md';
const calendarOutside = 'text-plum-200 aria-selected:text-plum-200';
const calendarHidden = 'invisible';

export const getCalendarStyles = (
  buttonVariant: ComponentProps<typeof Button>['variant'] = 'ghost'
): Record<string, string> => ({
  root: cn(defaultClassNames.root, calendarRoot),
  months: cn(defaultClassNames.months, calendarMonths),
  month: cn(defaultClassNames.month, calendarMonth),
  nav: cn(defaultClassNames.nav, calendarNav),
  button_previous: cn(
    defaultClassNames.button_previous,
    buttonVariants({ variant: buttonVariant }),
    calendarNavButton
  ),
  button_next: cn(
    defaultClassNames.button_next,
    buttonVariants({ variant: buttonVariant }),
    calendarNavButton
  ),
  month_caption: cn(defaultClassNames.month_caption, calendarMonthCaption),
  dropdowns: cn(defaultClassNames.dropdowns, calendarDropdowns),
  dropdown_root: cn(defaultClassNames.dropdown_root, calendarDropdownRoot),
  dropdown: cn(defaultClassNames.dropdown, calendarDropdown),
  month_grid: calendarTable,
  weekdays: cn(defaultClassNames.weekdays, calendarWeekdays),
  weekday: cn(defaultClassNames.weekday, calendarWeekday),
  week: cn(defaultClassNames.week, calendarWeek),
  week_number_header: cn(defaultClassNames.week_number_header, calendarWeekNumberHeader),
  week_number: cn(defaultClassNames.week_number, calendarWeekNumberCell),
  day: cn(defaultClassNames.day, calendarDay),
  range_start: cn(defaultClassNames.range_start, calendarRangeStart),
  range_middle: cn(defaultClassNames.range_middle, calendarRangeMiddle),
  range_end: cn(defaultClassNames.range_end, calendarRangeEnd),
  today: cn(defaultClassNames.today, calendarToday),
  outside: cn(defaultClassNames.outside, calendarOutside),
  disabled: defaultClassNames.disabled,
  hidden: cn(defaultClassNames.hidden, calendarHidden),
});
